'use client'

import { useEffect, useCallback, useRef } from 'react'

interface ErrorReport {
  message: string
  stack?: string
  url?: string
  componentStack?: string
  digest?: string
  metadata?: Record<string, unknown>
}

// Batch errors to reduce API calls
const ERROR_BATCH_DELAY_MS = 1000
const MAX_BATCH_SIZE = 5

// Track reported errors to avoid duplicates
const reportedErrors = new Set<string>()

/**
 * Extract the React error decoder URL from minified React error messages.
 * Production React replaces human-readable messages with codes like:
 *   "Minified React error #418; visit https://react.dev/errors/418?args[]=..."
 * The URL contains the full human-readable message.
 */
function extractReactErrorUrl(message: string): string | undefined {
  const match = message.match(/https:\/\/react\.dev\/errors\/\d+[^\s;]*/i)
  return match?.[0]
}

/**
 * Generate a simple fingerprint for deduplication
 */
function getErrorFingerprint(message: string, stack?: string): string {
  const firstLine = stack?.split('\n')[1]?.trim() || ''
  return `${message}|${firstLine}`
}

/**
 * Send error report to the server
 */
async function sendErrorReport(error: ErrorReport): Promise<void> {
  const fingerprint = getErrorFingerprint(error.message, error.stack)
  
  // Skip if we've already reported this error recently
  if (reportedErrors.has(fingerprint)) {
    return
  }
  
  reportedErrors.add(fingerprint)
  
  // Clear from set after 5 minutes to allow re-reporting if it persists
  setTimeout(() => {
    reportedErrors.delete(fingerprint)
  }, 5 * 60 * 1000)

  try {
    await fetch('/api/errors', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(error),
    })
  } catch {
    // Silently fail - we don't want error reporting to cause more errors
    console.debug('[ErrorReporter] Failed to send error report')
  }
}

/**
 * Report an error to the server
 */
export function reportError(
  error: Error,
  options?: {
    componentStack?: string
    digest?: string
    metadata?: Record<string, unknown>
  }
): void {
  sendErrorReport({
    message: error.message,
    stack: error.stack,
    url: typeof window !== 'undefined' ? window.location.href : undefined,
    componentStack: options?.componentStack,
    digest: options?.digest,
    metadata: options?.metadata,
  })
}

/**
 * Hook to set up global error listeners
 * Should be used once at the app root level
 */
export function useErrorReporter() {
  const batchRef = useRef<ErrorReport[]>([])
  const batchTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  const flushBatch = useCallback(() => {
    const batch = batchRef.current
    batchRef.current = []
    
    if (batchTimeoutRef.current) {
      clearTimeout(batchTimeoutRef.current)
      batchTimeoutRef.current = null
    }

    // Send each error in the batch
    batch.forEach(sendErrorReport)
  }, [])

  const queueError = useCallback((error: ErrorReport) => {
    batchRef.current.push(error)

    // Flush immediately if batch is full
    if (batchRef.current.length >= MAX_BATCH_SIZE) {
      flushBatch()
      return
    }

    // Otherwise, schedule a flush
    if (!batchTimeoutRef.current) {
      batchTimeoutRef.current = setTimeout(flushBatch, ERROR_BATCH_DELAY_MS)
    }
  }, [flushBatch])

  useEffect(() => {
    // Handle uncaught errors
    const handleError = (event: ErrorEvent) => {
      // Ignore errors from extensions or third-party scripts
      if (event.filename && !event.filename.includes(window.location.origin)) {
        return
      }

      // Skip cross-origin "Script error." — browsers strip all details from
      // cross-origin scripts, so these are undebuggable (no stack, no filename)
      if (event.message === 'Script error.' && !event.filename) {
        return
      }

      const errorMessage = event.message || 'Unknown error'
      queueError({
        message: errorMessage,
        stack: event.error?.stack,
        url: window.location.href,
        metadata: {
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
          reactErrorUrl: extractReactErrorUrl(errorMessage),
        },
      })
    }

    // Handle unhandled promise rejections
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      const error = event.reason
      const message = error instanceof Error
        ? error.message
        : typeof error === 'string'
          ? error
          : 'Unhandled promise rejection'

      // Skip Next.js internal control flow errors (redirect/notFound throw
      // special errors that Next.js catches — they're not real failures)
      if (message === 'NEXT_REDIRECT' || message === 'NEXT_NOT_FOUND') {
        return
      }

      const stack = error instanceof Error ? error.stack : undefined

      queueError({
        message,
        stack,
        url: window.location.href,
        metadata: {
          type: 'unhandledrejection',
          reactErrorUrl: extractReactErrorUrl(message),
        },
      })
    }

    // Add listeners
    window.addEventListener('error', handleError)
    window.addEventListener('unhandledrejection', handleUnhandledRejection)

    // Flush any pending errors on unmount
    return () => {
      window.removeEventListener('error', handleError)
      window.removeEventListener('unhandledrejection', handleUnhandledRejection)
      
      if (batchRef.current.length > 0) {
        flushBatch()
      }
    }
  }, [queueError, flushBatch])
}

/**
 * Component wrapper to initialize error reporting
 */
export function ErrorReporterProvider({ children }: { children: React.ReactNode }) {
  useErrorReporter()
  return children
}








